/**
 * What a pointer contact means: navigate the canvas, or draw on it (#40).
 *
 * This used to be an inline boolean in `PointerInput.handleDown`:
 *
 * ```ts
 * const pan = s.pointerType === "touch" || (mouse && (middle || space));
 * ```
 *
 * — which is why a finger could never draw. Every touch contact returned into
 * a pan before the `onDown` that starts a tool gesture, so the tools never saw
 * the finger at all. Nothing downstream needed changing; they already take a
 * `PointerSample` carrying `pointerType`.
 *
 * ## The rule
 *
 * **One finger draws. Two fingers navigate.**
 *
 * There is no mode, no preference and no auto-detection, because the two
 * gestures do not compete: two-finger pan / pinch is decided by the caller
 * before this is reached and is never taken away, so nothing is lost by
 * letting the single contact draw. A device with a stylus needs no setting
 * either — you draw with the stylus because it is in your hand, and the
 * `pointerType` on each event is a better answer than a mode could be.
 *
 * An earlier version of this asked whether a pen had been seen this session
 * and offered a user override on top. Both were answering a question that only
 * existed while one finger still had to choose between drawing and panning.
 * The resting-palm case they were really guarding is handled where it belongs,
 * in the palm rejection in `PointerInput` — a pen going down cancels a touch
 * draw already in flight.
 *
 * The one thing that does still differ by device is how big a target has to be
 * to be hit, and that is driven per event by `pointerType` rather than by any
 * mode: a fingertip is nearer 9 mm than the 6 px a stylus tip wants.
 */

export interface PointerGestureInput {
  pointerType: "mouse" | "pen" | "touch";
  /** True when the primary button is down — `sample.buttons & 1`. Always true
   *  for a touch contact; a pen draws on contact regardless. */
  primaryDown: boolean;
  /** Middle button, or space held with the left button — the mouse's own pan. */
  mousePanRequested: boolean;
  /** The `touch-device-support` feature flag. OFF reproduces the old behaviour
   *  exactly — every touch contact pans — so the flag is a true no-op, and the
   *  escape hatch if a finger drawing turns out wrong on real hardware. */
  touchDrawEnabled: boolean;
}

export type PointerGesture = "pan" | "draw" | "none";

/** Decide what a single pointer contact does. The caller handles the
 *  two-finger pinch before calling this, and handles palm rejection (a touch
 *  while a pen is down) before that. */
export function decidePointerGesture(
  input: PointerGestureInput,
): PointerGesture {
  if (input.pointerType === "pen") return "draw";

  if (input.pointerType === "mouse") {
    if (input.mousePanRequested) return "pan";
    return input.primaryDown ? "draw" : "none";
  }

  // Touch: one finger draws, because two fingers already navigate.
  return input.touchDrawEnabled ? "draw" : "pan";
}
